import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  MessageCircleHeart, BrainCircuit, Sparkles,
  ArrowRight, Play, BarChart3,
  ChevronRight, ChevronDown, Star, Zap, Users, Globe,
  Mic, Camera, BookOpen, HelpCircle, X, Check,
  ImageIcon, ClipboardCheck, HeartHandshake,
  TrendingUp, Target, Network, Flame, GraduationCap,
  Compass, AlertTriangle, Lightbulb, Rocket, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_ICON = 'https://static.prod-images.emergentagent.com/jobs/85ba9bbd-736c-4dbb-ae2d-37b84f64fb5b/images/46c4c99dc82a03ab2b247ccff337db0bc40f8a2e674401b4d8deca221940954b.png';
const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/38e90ad2-79e7-49db-b5b2-bdcedf4bdcda/images/2a5fd40443ac337ccefe48499e9ea22c223ac229c32e2e0e67bd5f4ad1cc7b21.png';
const CURIOSITY_IMG = 'https://static.prod-images.emergentagent.com/jobs/38e90ad2-79e7-49db-b5b2-bdcedf4bdcda/images/73a2154a2098689d6159c7296809328e1fdd07e23139d7dc4a8b14b10c1ac364.png';

/* --- Animation helpers --- */
const fadeUp = { hidden: { opacity: 0, y: 32 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

function Section({ children, className = '', id }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref} id={id}
      initial="hidden" animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      className={`relative z-10 ${className}`}
    >
      {children}
    </motion.section>
  );
}

/* --- Chat Mockup --- */
function ChatMockup() {
  return (
    <div className="bg-[#FFF9F0] rounded-2xl p-4 shadow-inner border border-orange-100 font-[Nunito]" data-testid="chat-mockup">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-orange-100">
        <img src={LOGO_ICON} alt="Yukti" className="w-6 h-6 rounded-lg" />
        <span className="text-sm font-semibold text-[#1F2937]">Yukti</span>
        <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full ml-auto">online</span>
      </div>
      <div className="space-y-2.5">
        <div className="flex justify-end">
          <div className="bg-[#C2410C] text-white text-xs px-3 py-1.5 rounded-2xl rounded-br-sm max-w-[75%]">Why is the sky blue?</div>
        </div>
        <div className="flex justify-start">
          <div className="bg-white text-[#1F2937] text-xs px-3 py-1.5 rounded-2xl rounded-bl-sm max-w-[80%] shadow-sm border border-orange-50">
            Sunlight has many colors hidden in it. When it hits our atmosphere, one color scatters more than others. Can you guess which one?
          </div>
        </div>
        <div className="flex justify-end">
          <div className="bg-[#C2410C] text-white text-xs px-3 py-1.5 rounded-2xl rounded-br-sm max-w-[75%]">Because blue has shorter waves?</div>
        </div>
        <div className="flex justify-start">
          <div className="bg-white text-[#1F2937] text-xs px-3 py-1.5 rounded-2xl rounded-bl-sm max-w-[80%] shadow-sm border border-orange-50">
            Exactly right! You figured it out yourself!
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border border-orange-100">
        <Mic className="w-3.5 h-3.5 text-[#9CA3AF]" />
        <span className="text-[10px] text-[#9CA3AF] flex-1">Ask Yukti anything...</span>
        <Camera className="w-3.5 h-3.5 text-[#9CA3AF]" />
      </div>
    </div>
  );
}

/* --- Analytics Mockup --- */
function AnalyticsMockup() {
  return (
    <div className="bg-[#FFF9F0] rounded-2xl p-4 shadow-inner border border-orange-100 font-[Nunito]" data-testid="analytics-mockup">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-orange-100">
        <span className="text-sm font-semibold text-[#1F2937]">Aarav's Insights</span>
        <span className="text-[10px] text-[#4B5563] bg-gray-100 px-2 py-0.5 rounded-full">This Week</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[
          { label: 'Curiosity', value: '87', color: 'text-[#C2410C]' },
          { label: 'Mastered', value: '6', color: 'text-[#15803D]' },
          { label: 'Revision', value: '2', color: 'text-[#DC2626]' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-lg p-2 text-center border border-orange-50">
            <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
            <div className="text-[9px] text-[#9CA3AF]">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-lg p-2.5 border border-orange-50 mb-2.5">
        <div className="text-[10px] font-semibold text-[#4B5563] mb-2">Quiz Mastery (Learning DNA)</div>
        <div className="space-y-1.5">
          {[
            { topic: 'Photosynthesis', w: '92%', color: 'bg-green-500', status: 'Mastered' },
            { topic: 'Fractions', w: '78%', color: 'bg-amber-500', status: 'Partial' },
            { topic: 'Indian Freedom', w: '45%', color: 'bg-red-400', status: 'Review' },
          ].map((t) => (
            <div key={t.topic} className="flex items-center gap-2">
              <span className="text-[9px] text-[#4B5563] w-20 truncate">{t.topic}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${t.color}`} style={{ width: t.w }} />
              </div>
              <span className="text-[8px] text-[#9CA3AF]">{t.status}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-lg p-2.5 border border-orange-50">
        <div className="text-[10px] font-semibold text-[#4B5563] mb-1.5">Topic Mind Map</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {['Science', 'Space', 'Math', 'History', 'Culture'].map((t, i) => (
            <span key={t} className="text-[8px] px-2 py-0.5 rounded-full border" style={{
              borderColor: ['#C2410C','#15803D','#7C3AED','#EA580C','#0284C7'][i],
              color: ['#C2410C','#15803D','#7C3AED','#EA580C','#0284C7'][i],
              background: ['#FFF7ED','#F0FDF4','#F5F3FF','#FFF7ED','#F0F9FF'][i]
            }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --- Child Home Mockup --- */
function ChildHomeMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-orange-100 overflow-hidden font-[Nunito]" data-testid="child-home-mockup">
      <div className="bg-gradient-to-r from-[#C2410C] to-[#EA580C] px-4 py-3 flex items-center gap-3">
        <img src={LOGO_ICON} alt="Yukti" className="w-7 h-7 rounded-lg bg-white/20 p-0.5" />
        <div>
          <div className="text-white text-sm font-bold">Yukti</div>
          <div className="text-white/70 text-[10px]">Your learning companion</div>
        </div>
        <span className="text-[9px] text-white/80 bg-white/20 px-2 py-0.5 rounded-full ml-auto">Online</span>
      </div>
      <div className="p-4">
        <p className="text-[#1F2937] text-sm mb-3">Hi Aarav! Ready to discover something amazing?</p>
        <div className="text-[10px] font-semibold text-[#4B5563] uppercase tracking-wider mb-2">How can Yukti help you today?</div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { name: 'Learn something new', icon: Compass, color: 'from-blue-400 to-indigo-500' },
            { name: 'Quiz me!', icon: BrainCircuit, color: 'from-purple-400 to-violet-500' },
          ].map((m) => (
            <div key={m.name} className="rounded-xl p-3 flex flex-col items-center gap-1.5 text-center bg-gradient-to-br text-white shadow-sm" style={{backgroundImage: `linear-gradient(135deg, var(--tw-gradient-stops))`}}>
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${m.color} flex items-center justify-center shadow-sm`}>
                <m.icon className="w-4 h-4 text-white" />
              </div>
              <span className="text-[10px] font-medium text-[#1F2937]">{m.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --- FAQ Accordion Item --- */
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border border-orange-100 rounded-xl overflow-hidden bg-white/80 backdrop-blur-sm" data-testid="faq-item">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-orange-50/50 transition-colors"
        data-testid="faq-toggle"
      >
        <span className="text-base font-semibold text-[#1F2937] font-[Outfit] pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-[#C2410C] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-5 text-sm text-[#4B5563] leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* --- Stat Pill for "The Real Gap" section --- */
function StatPill({ value, label, color }) {
  return (
    <div className="text-center">
      <div className={`text-3xl sm:text-4xl font-bold font-[Outfit] ${color}`}>{value}</div>
      <div className="text-xs sm:text-sm text-[#4B5563] mt-1">{label}</div>
    </div>
  );
}


/* ======================================= */
/*            LANDING PAGE                  */
/* ======================================= */
export default function LandingPage() {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [regStatus, setRegStatus] = useState(null);

  useEffect(() => {
    fetch(`/api/auth/registration-status`)
      .then(r => r.json())
      .then(data => setRegStatus(data))
      .catch(() => {});
  }, []);

  const startTrial = () => {
    if (regStatus?.is_full) return;
    navigate('/auth/parent');
  };

  const startStudentTrial = () => {
    if (regStatus?.is_full) return;
    navigate('/auth/student');
  };

  const steps = [
    { num: '01', title: 'Sign up as a parent', desc: "Create your account and add your child's profile with their name, age, board, and interests.", icon: Users },
    { num: '02', title: 'Let your child explore', desc: 'Yukti greets them and offers learning modes tailored to their age and curriculum.', icon: Zap },
    { num: '03', title: 'Track their growth', desc: 'Review insights, quiz mastery, topic maps, and curiosity trends on your parent dashboard.', icon: BarChart3 },
  ];

  const faqs = [
    {
      q: 'What is Yukti and how is it different?',
      a: 'Yukti is a Socratic AI tutor designed for children aged 5 to 15. Unlike tools that give direct answers, Yukti guides your child through questioning to help them reason and discover answers themselves. It is parent-controlled, age-adapted, curriculum-aligned to CBSE/ICSE/NCERT, and tracks your child\'s curiosity growth with a dedicated analytics dashboard.'
    },
    {
      q: 'What does the Socratic method mean in practice?',
      a: 'Instead of answering "The sky is blue because of Rayleigh scattering," Yukti asks "Sunlight has many colors. When it hits our atmosphere, one scatters more. Can you guess which one and why?" This builds deep understanding, not surface-level memorization.'
    },
    {
      q: 'What ages, boards, and subjects does Yukti support?',
      a: 'Yukti supports children from age 5 to 15 across CBSE, ICSE, and State Boards. Content and interaction styles are adapted for five age bands. It covers all subjects including Science, Math, History, General Knowledge, and more, with all content anchored to NCERT frameworks.'
    },
    {
      q: 'Can my child use Yukti independently?',
      a: "Yes! Once you set up their profile, your child can explore freely. Yukti offers age-appropriate learning modes. You can monitor everything from your parent dashboard, including full chat history, quiz mastery data, and topic mind maps."
    },
    {
      q: 'What languages does Yukti support?',
      a: 'Yukti responds in whatever language your child types in, including Hindi, English, Tamil, Bengali, Telugu, and more. The experience feels natural and culturally relevant, making learning feel like home.'
    },
    {
      q: 'How does the Parent Analytics dashboard work?',
      a: 'The dashboard gives you a complete picture: an AI-generated weekly summary, a curiosity score, quiz mastery tracking that shows which concepts your child has mastered vs needs revision, a visual topic mind map, a 30-day activity heatmap, and full expandable chat history. You see how your child thinks, not just what they scored.'
    },
  ];

  return (
    <div className="min-h-screen overflow-hidden font-[Nunito]" style={{ background: 'linear-gradient(135deg, #FFFBF5 0%, #FFF7ED 50%, #FEF3C7 100%)' }}>

      {/* --- Floating Nav --- */}
      <nav className="fixed top-4 left-0 right-0 mx-auto max-w-5xl bg-white/70 backdrop-blur-xl border border-white/40 shadow-sm rounded-full px-6 md:px-8 py-3 z-50 flex items-center justify-between" data-testid="navbar">
        <div className="flex items-center gap-2">
          <img src={LOGO_ICON} alt="Yukti" className="w-8 h-8" data-testid="nav-logo" />
          <span className="text-xl font-bold text-[#1F2937] font-[Outfit]">Yukti</span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm text-[#4B5563] font-medium">
          <a href="#the-gap" className="hover:text-[#C2410C] transition-colors" data-testid="nav-gap">The Real Gap</a>
          <a href="#socratic" className="hover:text-[#C2410C] transition-colors" data-testid="nav-socratic">The Socratic Way</a>
          <a href="#features" className="hover:text-[#C2410C] transition-colors" data-testid="nav-features">Features</a>
          <a href="#faq" className="hover:text-[#C2410C] transition-colors" data-testid="nav-faq">FAQ</a>
        </div>
        <Button
          onClick={() => document.getElementById('hero-section')?.scrollIntoView({ behavior: 'smooth' })}
          className="bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-full px-5 py-2 text-sm font-semibold shadow-md hover:shadow-lg transition-all hover:scale-105 active:scale-95"
          data-testid="nav-get-started-btn"
        >
          Get Started
        </Button>
      </nav>

      {/* --- Hero --- */}
      <section id="hero-section" className="relative z-10 pt-28 md:pt-36 pb-16 md:pb-24 px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 bg-[#C2410C]/10 text-[#C2410C] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                The Socratic AI Tutor for Indian Families
              </div>
              <p className="text-sm md:text-base text-[#9A3412] font-medium font-[Outfit] tracking-wide mb-4" data-testid="sanskrit-meaning">
                <span className="text-lg">&#x092F;&#x0941;&#x0915;&#x094D;&#x0924;&#x093F;</span> Logic. Reasoning. Skill. Strategic Planning.
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-[#1F2937] leading-[1.15] tracking-tight mb-6 font-[Outfit]">
                Understand your child,{' '}
                <span className="text-[#C2410C]">not just their grades.</span>
              </h1>
              <p className="text-base md:text-lg text-[#4B5563] leading-relaxed mb-4 max-w-xl">
                Yukti doesn't hand out answers. It teaches your child to discover them through guided Socratic questioning, the way the greatest teachers have taught for thousands of years.
              </p>
              <p className="text-sm md:text-base text-[#9A3412]/80 font-medium mb-8 max-w-xl italic">
                Know what your child thinks, not just what they score.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Button
                  onClick={startTrial}
                  disabled={regStatus?.is_full}
                  className="bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-full px-8 py-6 text-base font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  data-testid="hero-parent-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {regStatus?.is_full ? 'Early Access Full' : "I'm a Parent"}
                </Button>
                <Button
                  onClick={startStudentTrial}
                  disabled={regStatus?.is_full}
                  className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-8 py-6 text-base font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  data-testid="hero-student-btn"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  {regStatus?.is_full ? 'Early Access Full' : "I'm a Student"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-2 border-[#C2410C]/20 text-[#C2410C] hover:bg-[#C2410C]/5 hover:border-[#C2410C]/40 rounded-full px-8 py-6 text-base font-semibold"
                  data-testid="hero-see-features-btn"
                >
                  See Features
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="flex items-center gap-4 text-sm text-[#9CA3AF]">
                {regStatus && !regStatus.is_full && regStatus.spots_remaining <= 50 && (
                  <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-200" data-testid="spots-remaining-badge">
                    <Zap className="w-3 h-3" />
                    {regStatus.spots_remaining} spots left in early access
                  </div>
                )}
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-white shadow-sm flex items-center justify-center" style={{ background: ['#C2410C','#15803D','#7C3AED','#EA580C'][i-1] + '22' }}>
                      <Star className="w-3 h-3" style={{ color: ['#C2410C','#15803D','#7C3AED','#EA580C'][i-1] }} />
                    </div>
                  ))}
                </div>
                <span>Ages 5 to 15 &bull; CBSE, ICSE & NCERT</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img src={HERO_IMG} alt="Parent and child learning together with Yukti" className="w-full h-auto object-cover" loading="eager" data-testid="hero-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FFFBF5]/40 to-transparent" />
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.8 }}
                className="absolute -bottom-4 -left-4 md:left-4 bg-white rounded-2xl px-4 py-3 shadow-lg border border-orange-100"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                    <BrainCircuit className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#1F2937]">Curiosity Score</div>
                    <div className="text-sm font-bold text-green-600">87/100</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- "The Real Gap" --- Narrative Section --- */}
      <Section className="px-6 md:px-12 py-16 md:py-24" id="the-gap">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <AlertTriangle className="w-3.5 h-3.5" />
              The Real Gap
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#1F2937] tracking-tight font-[Outfit] mb-4">
              Schooling is not the same as <span className="text-[#C2410C]">learning.</span>
            </h2>
            <p className="text-base md:text-lg text-[#4B5563] max-w-3xl mx-auto leading-relaxed">
              India has over 260 million school children. Most are taught to memorize, not to understand. NEP 2020 recognized this crisis and mandated a shift from rote to reasoning. The data tells the rest of the story.
            </p>
          </motion.div>

          {/* Stats Row */}
          <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-12 md:mb-16">
            <StatPill value="1 in 4" label="Class 3 students can read a Class 2 text" color="text-red-600" />
            <StatPill value="50%" label="of CBSE questions are now competency-based" color="text-[#C2410C]" />
            <StatPill value="30%" label="higher scores when students explain, not memorize" color="text-[#15803D]" />
            <StatPill value="40%" label="more accurate on new problems with conceptual learning" color="text-[#0284C7]" />
          </motion.div>

          {/* The Emotional Hook */}
          <motion.div variants={fadeUp} className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="relative bg-red-50/60 border border-red-100 rounded-2xl p-6 md:p-8" data-testid="memorize-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <X className="w-4 h-4 text-red-500" />
                </div>
                <span className="text-sm font-bold text-red-600 uppercase tracking-wider font-[Outfit]">Memorized Under Pressure</span>
              </div>
              <p className="text-[#1F2937] text-base md:text-lg leading-relaxed font-medium mb-3">
                What you memorize under pressure, you forget under every pressure.
              </p>
              <p className="text-sm text-[#4B5563] leading-relaxed">
                Cramming for exams produces short-term results. When the format changes, when the question is unfamiliar, memorizers freeze. Research shows they feel more overwhelmed and perform worse under exam stress.
              </p>
            </div>

            <div className="relative bg-green-50/60 border border-green-200 rounded-2xl p-6 md:p-8" data-testid="understand-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-sm font-bold text-green-700 uppercase tracking-wider font-[Outfit]">Understood Through Curiosity</span>
              </div>
              <p className="text-[#1F2937] text-base md:text-lg leading-relaxed font-medium mb-3">
                What you understand without pressure, you recall under every pressure.
              </p>
              <p className="text-sm text-[#4B5563] leading-relaxed">
                Curiosity-driven learning creates durable knowledge. Students who explore, question, and explain concepts in their own words retain far longer and perform better, even on questions they've never seen before.
              </p>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="text-center mt-10">
            <p className="text-lg md:text-xl text-[#1F2937] font-semibold font-[Outfit] max-w-2xl mx-auto">
              Yukti exists because your child deserves to <span className="text-[#C2410C]">understand</span>, not just remember.
            </p>
            <p className="text-xs text-[#9CA3AF] mt-3">Sources: ASER 2024, NEP 2020, Science (2019), Cognitive Science (2020)</p>
          </motion.div>
        </div>
      </Section>

      {/* --- The Socratic Way --- */}
      <Section className="px-6 md:px-12 py-16 md:py-24" id="socratic">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-[#C2410C]/10 text-[#C2410C] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              Why Socratic Learning?
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#1F2937] tracking-tight font-[Outfit] mb-3">
              Questions that build thinkers,{' '}
              <span className="text-[#C2410C]">not just test-takers.</span>
            </h2>
            <p className="text-base md:text-lg text-[#4B5563] max-w-2xl mx-auto">
              Most tools rush to give answers. Yukti follows the Socratic method, guiding your child to reason, question, and discover on their own.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6" data-testid="socratic-comparison">
            {/* The Old Way */}
            <motion.div variants={fadeUp} className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 md:p-8 relative" data-testid="old-way-card">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <X className="w-4 h-4 text-gray-400" />
                </div>
                <span className="text-sm font-bold text-gray-400 uppercase tracking-wider">The Old Way</span>
              </div>
              <div className="space-y-3 mb-5">
                <div className="flex justify-end">
                  <div className="bg-gray-200 text-gray-700 text-xs px-3 py-1.5 rounded-2xl rounded-br-sm max-w-[75%]">Why is the sky blue?</div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-gray-50 text-gray-600 text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%] border border-gray-100">
                    The sky is blue because molecules in the atmosphere scatter blue light from the sun more than other colors. This is called Rayleigh scattering. The shorter wavelength of blue light causes it to scatter more efficiently.
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                  Answer memorized. Forgotten tomorrow.
                </div>
              </div>
            </motion.div>

            {/* Yukti's Way */}
            <motion.div variants={fadeUp} className="bg-white/80 backdrop-blur-sm border-2 border-[#C2410C]/20 rounded-2xl p-6 md:p-8 relative ring-1 ring-[#C2410C]/10" data-testid="yukti-socratic-card">
              <div className="absolute -top-3 right-6 bg-[#C2410C] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Yukti's Way</div>
              <div className="flex items-center gap-2 mb-5">
                <img src={LOGO_ICON} alt="Yukti" className="w-8 h-8" />
                <span className="text-sm font-bold text-[#C2410C] uppercase tracking-wider">The Socratic Method</span>
              </div>
              <div className="space-y-3 mb-5">
                <div className="flex justify-end">
                  <div className="bg-[#C2410C] text-white text-xs px-3 py-1.5 rounded-2xl rounded-br-sm max-w-[75%]">Why is the sky blue?</div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-[#FFF7ED] text-[#1F2937] text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%] border border-orange-100">
                    Sunlight has many colors hidden in it. When it hits our atmosphere, one color scatters more than others. Can you guess which one and why?
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-[#C2410C] text-white text-xs px-3 py-1.5 rounded-2xl rounded-br-sm max-w-[75%]">Blue? Because it has shorter waves?</div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-[#FFF7ED] text-[#1F2937] text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%] border border-orange-100">
                    Exactly right! You figured it out yourself, that's the best kind of learning!
                  </div>
                </div>
              </div>
              <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                <div className="flex items-center gap-2 text-xs text-green-700 font-medium">
                  <Check className="w-3.5 h-3.5" />
                  Understanding earned. Remembered forever.
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* --- Features --- */}
      <Section className="px-6 md:px-12 py-16 md:py-24" id="features">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} className="mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-[#C2410C]/10 text-[#C2410C] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              Platform Features
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#1F2937] tracking-tight font-[Outfit] mb-3">
              Everything your child needs.<br className="hidden sm:block" />
              <span className="text-[#C2410C]">Everything you want to know.</span>
            </h2>
            <p className="text-base md:text-lg text-[#4B5563] max-w-2xl">
              A tutor that builds understanding, and a dashboard that reveals every insight.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5 md:gap-6">

            {/* Feature 1: Socratic AI Tutor */}
            <motion.div variants={fadeUp} className="bg-white/80 backdrop-blur-sm border border-orange-100 rounded-2xl p-6 md:p-8 hover:border-orange-200 transition-colors" data-testid="feature-chat-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#FFF7ED]">
                  <MessageCircleHeart className="w-4 h-4 text-[#C2410C]" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#C2410C]">AI Tutor</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1F2937] mb-2 font-[Outfit]">Your Child's Thinking Buddy</h3>
              <p className="text-sm text-[#4B5563] mb-4 leading-relaxed">A Socratic AI companion that never gives direct answers. Instead, it asks the right questions to spark reasoning across homework, textbooks, quizzes, and open exploration.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                {[
                  { icon: MessageCircleHeart, text: 'Socratic Conversations', detail: 'Guided questioning across all subjects' },
                  { icon: GraduationCap, text: 'Board-Aligned', detail: 'CBSE, ICSE & NCERT curriculum' },
                  { icon: ImageIcon, text: 'Textbook Scanner', detail: 'Snap a page, get guided learning' },
                  { icon: Mic, text: 'Voice & Image Input', detail: 'Talk or show, Yukti understands' },
                ].map((cap) => (
                  <div key={cap.text} className="flex items-start gap-2 bg-[#FFF7ED] rounded-lg px-3 py-2 border border-orange-50">
                    <cap.icon className="w-3.5 h-3.5 text-[#C2410C] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-semibold text-[#1F2937]">{cap.text}</div>
                      <div className="text-[10px] text-[#9CA3AF]">{cap.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
              <ChatMockup />
            </motion.div>

            {/* Feature 2: Quiz Mode */}
            <motion.div variants={fadeUp} className="bg-white/80 backdrop-blur-sm border border-orange-100 rounded-2xl p-6 md:p-8 hover:border-orange-200 transition-colors" data-testid="feature-quiz-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#F5F3FF]">
                  <BrainCircuit className="w-4 h-4 text-[#7C3AED]" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#7C3AED]">Quiz Mode</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1F2937] mb-2 font-[Outfit]">Tests Understanding, Not Memory</h3>
              <p className="text-sm text-[#4B5563] mb-4 leading-relaxed">An adaptive quiz engine that adjusts difficulty in real-time. It doesn't just test, it teaches through the quiz itself, tracking exactly what your child knows and where they need help.</p>
              <div className="space-y-2 mb-5">
                {[
                  { icon: TrendingUp, text: 'Adaptive Difficulty', detail: 'Levels 1-5, adjusts to your child' },
                  { icon: Target, text: 'Learning DNA', detail: 'Tracks strong & weak concepts precisely' },
                  { icon: GraduationCap, text: 'Board-Aligned MCQs', detail: 'Questions anchored to CBSE/ICSE syllabus' },
                  { icon: Lightbulb, text: 'Hints, Not Answers', detail: 'One clue per question, never the answer' },
                ].map((cap) => (
                  <div key={cap.text} className="flex items-center gap-3 bg-[#F5F3FF] rounded-lg px-3 py-2.5 border border-purple-50">
                    <cap.icon className="w-4 h-4 text-[#7C3AED] flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-[#1F2937]">{cap.text}</div>
                      <div className="text-[10px] text-[#9CA3AF]">{cap.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Quiz Mockup */}
              <div className="bg-[#F5F3FF] rounded-2xl p-4 border border-purple-100">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-purple-100">
                  <BrainCircuit className="w-4 h-4 text-[#7C3AED]" />
                  <span className="text-xs font-semibold text-[#7C3AED]">Quiz: Photosynthesis</span>
                  <span className="text-[9px] bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full ml-auto">Level 3</span>
                </div>
                <p className="text-xs text-[#1F2937] mb-3">Q3: A plant is kept in a dark room for 48 hours. What happens to its starch production?</p>
                <div className="space-y-1.5 mb-3">
                  {['(A) Increases rapidly', '(B) Stops completely', '(C) Stays the same', '(D) Doubles'].map((opt, i) => (
                    <div key={opt} className={`text-[11px] px-3 py-1.5 rounded-lg border ${i === 1 ? 'bg-green-50 border-green-200 text-green-700 font-semibold' : 'bg-white border-gray-100 text-[#4B5563]'}`}>
                      {opt} {i === 1 && <Check className="w-3 h-3 inline ml-1" />}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-[#7C3AED]" style={{ width: '60%' }} />
                  </div>
                  <span className="text-[9px] text-[#9CA3AF]">3/5 correct</span>
                </div>
              </div>
            </motion.div>

            {/* Feature 3: Parent Analytics */}
            <motion.div variants={fadeUp} className="bg-white/80 backdrop-blur-sm border border-orange-100 rounded-2xl p-6 md:p-8 hover:border-orange-200 transition-colors" data-testid="feature-analytics-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#F0FDF4]">
                  <BarChart3 className="w-4 h-4 text-[#15803D]" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#15803D]">Parent Analytics</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1F2937] mb-2 font-[Outfit]">See How Your Child Thinks</h3>
              <p className="text-sm text-[#4B5563] mb-4 leading-relaxed">A live analytics dashboard that goes beyond grades. See what your child is curious about, what they've mastered, and where they need help, all in one place.</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {[
                  { icon: Sparkles, text: 'AI Weekly Summary', color: 'text-orange-500', bg: 'bg-orange-50' },
                  { icon: Target, text: 'Quiz Mastery DNA', color: 'text-purple-500', bg: 'bg-purple-50' },
                  { icon: Network, text: 'Topic Mind Map', color: 'text-blue-500', bg: 'bg-blue-50' },
                  { icon: Flame, text: '30-Day Heatmap', color: 'text-red-500', bg: 'bg-red-50' },
                  { icon: MessageCircleHeart, text: 'Full Chat History', color: 'text-teal-600', bg: 'bg-teal-50' },
                  { icon: TrendingUp, text: 'Curiosity Score', color: 'text-green-600', bg: 'bg-green-50' },
                ].map((usp) => (
                  <div key={usp.text} className={`flex items-center gap-2 ${usp.bg} rounded-lg px-3 py-2 border border-gray-100`}>
                    <usp.icon className={`w-3.5 h-3.5 ${usp.color} flex-shrink-0`} />
                    <span className="text-[11px] font-semibold text-[#1F2937]">{usp.text}</span>
                  </div>
                ))}
              </div>
              <AnalyticsMockup />
            </motion.div>

            {/* Feature 4: Multi-lingual */}
            <motion.div variants={fadeUp} className="bg-white/80 backdrop-blur-sm border border-orange-100 rounded-2xl p-6 md:p-8 hover:border-orange-200 transition-colors" data-testid="feature-multilingual-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#F0F9FF]">
                  <Globe className="w-4 h-4 text-[#0284C7]" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#0284C7]">Multi-lingual</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1F2937] mb-2 font-[Outfit]">Learns in Your Language</h3>
              <p className="text-sm text-[#4B5563] mb-3 leading-relaxed">Yukti speaks your child's language. Whether they type in Hindi, English, or any other language, the AI tutor responds naturally in the same language, making learning feel like home.</p>
              <ul className="space-y-1.5 mb-4">
                {[
                  'Hindi, English, Tamil, Bengali & more',
                  'Natural language switching mid-conversation',
                  'Board-aligned: CBSE, ICSE, State Boards',
                  'Culturally contextual Indian examples',
                ].map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-[#4B5563]">
                    <Zap className="w-3.5 h-3.5 text-[#0284C7] flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="bg-[#F0F9FF] rounded-2xl p-4 border border-sky-100">
                <div className="space-y-2.5">
                  <div className="flex justify-end">
                    <div className="bg-[#0284C7] text-white text-xs px-3 py-1.5 rounded-2xl rounded-br-sm max-w-[80%]">
                      Mujhe batao ki chand dharti se kitna door hai?
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-white text-[#1F2937] text-xs px-3 py-1.5 rounded-2xl rounded-bl-sm max-w-[85%] shadow-sm border border-sky-50">
                      Bahut accha sawaal! Sochke batao, agar tum rocket mein jaate, toh kitna time lagega wahan pahunchne mein?
                    </div>
                  </div>
                </div>
                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                  {['Hindi', 'English', 'Tamil', 'Bengali', 'Telugu'].map((lang, i) => (
                    <span key={lang} className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${i === 0 ? 'bg-[#0284C7] text-white' : 'bg-white text-[#4B5563] border border-gray-200'}`}>
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Dream Missions — Coming Soon Teaser */}
          <motion.div variants={fadeUp} className="mt-6">
            <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border-2 border-dashed border-[#C2410C]/30 rounded-2xl p-6 md:p-8 relative overflow-hidden" data-testid="feature-dream-missions-card">
              <div className="absolute top-4 right-4 bg-[#C2410C] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Coming Soon
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#C2410C]/10">
                      <Target className="w-4 h-4 text-[#C2410C]" />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#C2410C]">Dream Missions</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#1F2937] mb-2 font-[Outfit]">From dreams to syllabus, one mission at a time.</h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                    Yukti starts from your child's dream role and maps it to their class syllabus, creating weekly Socratic missions that turn every chapter into training for their future.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Dream Discovery', 'Syllabus Mapping', 'Weekly Missions', 'Future-Ready Learning'].map((tag) => (
                      <span key={tag} className="text-[11px] px-3 py-1 rounded-full bg-white border border-orange-100 text-[#4B5563] font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
                <div className="w-full md:w-48 flex-shrink-0">
                  <img src={CURIOSITY_IMG} alt="Child exploring with wonder" className="w-full h-auto rounded-xl shadow-md" loading="lazy" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* --- Parent Hook Section --- */}
      <Section className="px-6 md:px-12 py-16 md:py-24" id="parent-hook">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} className="bg-gradient-to-br from-[#FFF7ED] via-white to-[#F0FDF4] border border-orange-100 rounded-3xl p-6 sm:p-8 md:p-12 text-center relative overflow-hidden" data-testid="parent-hook-section">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#C2410C]/3 rounded-full -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#15803D]/3 rounded-full translate-y-1/3 -translate-x-1/4" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-[#C2410C]/10 text-[#C2410C] text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
                <HeartHandshake className="w-3.5 h-3.5" />
                A Note for Parents
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1F2937] tracking-tight font-[Outfit] mb-6 max-w-3xl mx-auto leading-tight">
                Your child's real report card{' '}
                <span className="text-[#C2410C]">isn't their marks.</span>
              </h2>
              <div className="space-y-4 max-w-2xl mx-auto mb-8">
                <p className="text-base md:text-lg text-[#4B5563] leading-relaxed">
                  Ask them what they <span className="font-semibold text-[#1F2937]">learned</span> today, not what they scored. The questions they ask matter more than the answers they give.
                </p>
                <p className="text-base md:text-lg text-[#4B5563] leading-relaxed">
                  The parents who nurture <span className="font-semibold text-[#1F2937]">curious</span> children, build unstoppable adults.
                </p>
                <p className="text-sm text-[#9A3412] font-medium italic mt-4">
                  Yukti helps you see how your child thinks, their curiosity, their questions, their growth. Not just a number on a report card.
                </p>
              </div>
              <Button
                onClick={startTrial}
                disabled={regStatus?.is_full}
                className="bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-full px-5 sm:px-8 py-5 text-sm sm:text-base font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 max-w-full"
                data-testid="parent-hook-cta-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2 flex-shrink-0" />
                <span>Track Their Curiosity, Not Just Grades</span>
              </Button>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* --- Real Interface Preview --- */}
      <Section className="px-6 md:px-12 py-16 md:py-24" id="preview">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] tracking-tight font-[Outfit] mb-3">
              A Glimpse Inside <span className="text-[#C2410C]">Yukti</span>
            </h2>
            <p className="text-base md:text-lg text-[#4B5563] max-w-2xl mx-auto">
              This is how your child's learning home looks. Personal, warm, and built to spark curiosity every single day.
            </p>
          </motion.div>
          <motion.div variants={fadeUp} className="max-w-sm mx-auto">
            <ChildHomeMockup />
          </motion.div>
        </div>
      </Section>

      {/* --- How It Works --- */}
      <Section className="px-6 md:px-12 py-16 md:py-24" id="how-it-works">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-[#15803D]/10 text-[#15803D] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              Simple Setup
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] tracking-tight font-[Outfit]">
              Get started in <span className="text-[#C2410C]">3 minutes</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={step.num} variants={fadeUp}
                className="relative bg-white/80 backdrop-blur-sm border border-orange-100 rounded-2xl p-6 md:p-8 text-center hover:border-orange-200 transition-colors"
                data-testid={`step-card-${i}`}
              >
                <div className="text-5xl font-bold text-[#C2410C]/10 font-[Outfit] mb-2">{step.num}</div>
                <div className="w-12 h-12 mx-auto rounded-xl bg-[#C2410C]/10 flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-[#C2410C]" />
                </div>
                <h3 className="text-lg font-bold text-[#1F2937] mb-2 font-[Outfit]">{step.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{step.desc}</p>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-20">
                    <ChevronRight className="w-6 h-6 text-[#C2410C]/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* --- FAQ --- */}
      <Section className="px-6 md:px-12 py-16 md:py-24" id="faq">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-[#C2410C]/10 text-[#C2410C] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <HelpCircle className="w-3.5 h-3.5" />
              Common Questions
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937] tracking-tight font-[Outfit]">
              Frequently Asked <span className="text-[#C2410C]">Questions</span>
            </h2>
          </motion.div>
          <motion.div variants={fadeUp} className="space-y-3" data-testid="faq-list">
            {faqs.map((faq, i) => (
              <FAQItem
                key={i}
                question={faq.q}
                answer={faq.a}
                isOpen={openFAQ === i}
                onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
              />
            ))}
          </motion.div>
        </div>
      </Section>

      {/* --- Final CTA --- */}
      <Section className="px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div variants={fadeUp} className="relative bg-[#C2410C] rounded-3xl p-10 md:p-14 text-center overflow-hidden" data-testid="final-cta-section">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4" />
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight font-[Outfit] mb-4">
                Clarity for parents.<br />Confidence for children.
              </h2>
              <p className="text-base md:text-lg text-white/85 max-w-xl mx-auto mb-8">
                Join families across India who are building their children's thinking skills, not just test scores, with Yukti.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  onClick={startTrial}
                  disabled={regStatus?.is_full}
                  className="bg-white text-[#C2410C] hover:bg-gray-50 rounded-full px-10 py-6 text-base font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="final-parent-btn"
                >
                  <Users className="w-5 h-5 mr-2" />
                  {regStatus?.is_full ? 'Early Access Full' : "I'm a Parent"}
                </Button>
                <Button
                  onClick={startStudentTrial}
                  disabled={regStatus?.is_full}
                  className="bg-white/15 text-white hover:bg-white/25 border border-white/30 rounded-full px-10 py-6 text-base font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="final-student-btn"
                >
                  <GraduationCap className="w-5 h-5 mr-2" />
                  {regStatus?.is_full ? 'Early Access Full' : "I'm a Student"}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* --- Footer --- */}
      <footer className="relative z-10 px-6 md:px-12 py-10 border-t border-orange-100/50" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_ICON} alt="Yukti" className="w-7 h-7" />
            <span className="text-lg font-bold text-[#1F2937] font-[Outfit]">Yukti</span>
          </div>
          <p className="text-sm text-[#9CA3AF]">
            &copy; 2026 Yukti. Building tomorrow's thinkers, today.
          </p>
        </div>
      </footer>
    </div>
  );
}
